import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Trash2, Truck, X } from 'lucide-react';
import { Button } from '../../../../../components/ui/Button';
import { ConfirmDialog } from '../../../../../components/ui/ConfirmDialog';
import { Switch } from '../../../../../components/ui/Switch';
import {
  buildSettingCourierConfiguration,
  deleteSellerCourier,
  updateSellerCourier,
} from '../../../../../features/sellers/sellerDetailsThunks';
import { clearCourierError } from '../../../../../features/sellers/sellerDetailsSlice';
import { selectIsRunning } from '../../../../../features/feedback/feedbackSlice';
import { AddSellerCourierModal } from './AddSellerCourierModal';

const th = 'px-6 py-3 text-[11px] font-semibold text-text-muted uppercase tracking-wider text-left';
const td = 'px-6 py-4 text-sm text-ninja-dark dark:text-white';

// A component of its own so each switch reads its own request: the row has two,
// and only the one clicked should spin. The key is the one `updateSellerCourier`
// declares.
const CourierSwitch = ({ settingCourierId, flag, ...rest }) => {
  const saving = useSelector(selectIsRunning(updateSellerCourier, `${settingCourierId}:${flag}`));
  return <Switch loading={saving} {...rest} />;
};

/**
 * The courier section of the legacy `CompanyDetail`: per courier, whether it
 * works in stand-by and whether it needs pickup requests. The second switch is
 * what lets this seller's pickups be requested from "Solicitudes de retiro".
 *
 * @param {object} props
 * @param {number} props.companyId
 */
export const SellerCouriersTab = ({ companyId }) => {
  const dispatch = useDispatch();
  const { settingCouriers, courierError, courierBusyId } = useSelector((state) => state.sellerDetails);

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const handleToggle = (settingCourier, flag, errorMessage) => {
    dispatch(updateSellerCourier({
      companyId,
      settingCourierId: settingCourier.id,
      configuration: buildSettingCourierConfiguration(settingCourier, {
        [flag]: !settingCourier.configuration?.[flag],
      }),
      errorMessage,
      flag,
    }));
  };

  // The legacy deleted on the first click.
  const handleDelete = async () => {
    await dispatch(deleteSellerCourier({ companyId, settingCourierId: toDelete.id }));
    setToDelete(null);
  };

  return (
    <div
      data-testid="seller-couriers-tab"
      className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border overflow-hidden animate-in slide-in-from-bottom-2 duration-300"
    >
      <div className="p-4 border-b border-border flex items-center justify-between gap-4">
        <h3 className="text-base font-bold text-ninja-dark dark:text-white flex items-center gap-2">
          <Truck size={18} className="text-text-muted" />
          Configuración Stand-by de Couriers ({settingCouriers.length})
        </h3>
        <Button
          variant="ghost"
          size="sm"
          icon={Plus}
          onClick={() => setIsAddOpen(true)}
          data-testid="seller-courier-add"
        >
          Agregar
        </Button>
      </div>

      {courierError && !isAddOpen && (
        <div className="mx-4 mt-4 flex items-center justify-between gap-4 bg-red-50 dark:bg-red-900/20 text-danger px-4 py-3 rounded-xl border border-red-200 dark:border-red-900 text-sm font-medium">
          <span>{courierError}</span>
          <button type="button" aria-label="Cerrar error" onClick={() => dispatch(clearCourierError())}>
            <X size={16} />
          </button>
        </div>
      )}

      {settingCouriers.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead className="bg-ninja-gray dark:bg-[#0f0f0f]">
              <tr>
                <th className={th}>Courier</th>
                <th className={th}>Stand-by</th>
                <th className={th}>Solicitudes de retiro</th>
                <th className={th}>
                  <span className="sr-only">Acciones</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {settingCouriers.map((settingCourier) => {
                const standBy = settingCourier.configuration?.stand_by_enabled ?? false;
                const pickups = settingCourier.configuration?.requires_pickup_requests ?? false;
                const busy = courierBusyId === settingCourier.id;

                return (
                  <tr
                    key={settingCourier.id}
                    data-testid="seller-courier-row"
                    data-courier={settingCourier.courier}
                    className="border-t border-border"
                  >
                    <td className={`${td} font-bold`}>{settingCourier.courier}</td>
                    <td className={td}>
                      <CourierSwitch
                        settingCourierId={settingCourier.id}
                        flag="stand_by_enabled"
                        checked={standBy}
                        onChange={() => handleToggle(settingCourier, 'stand_by_enabled', 'Error al actualizar el stand-by')}
                        label={standBy ? 'Activo' : 'Inactivo'}
                        ariaLabel={`Stand-by de ${settingCourier.courier}`}
                        disabled={busy}
                        testId="seller-courier-standby"
                      />
                    </td>
                    <td className={td}>
                      <CourierSwitch
                        settingCourierId={settingCourier.id}
                        flag="requires_pickup_requests"
                        checked={pickups}
                        onChange={() => handleToggle(settingCourier, 'requires_pickup_requests', 'Error al actualizar solicitudes de retiro')}
                        label={pickups ? 'Activo' : 'Inactivo'}
                        ariaLabel={`Solicitudes de retiro de ${settingCourier.courier}`}
                        disabled={busy}
                        testId="seller-courier-pickups"
                      />
                    </td>
                    <td className={`${td} text-right`}>
                      <Button
                        variant="subtle"
                        size="sm"
                        iconOnly
                        icon={Trash2}
                        title="Eliminar"
                        aria-label={`Eliminar configuración de ${settingCourier.courier}`}
                        className="hover:text-danger"
                        disabled={busy}
                        onClick={() => setToDelete(settingCourier)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="p-6 text-sm text-text-secondary">
          No hay configuraciones de courier. Agrega una con el botón de arriba.
        </p>
      )}

      <AddSellerCourierModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        companyId={companyId}
      />

      <ConfirmDialog
        isOpen={Boolean(toDelete)}
        onClose={() => courierBusyId === null && setToDelete(null)}
        onConfirm={handleDelete}
        title="Eliminar configuración"
        message={toDelete ? `Se elimina la configuración de ${toDelete.courier} para este seller.` : ''}
        loading={Boolean(toDelete) && courierBusyId === toDelete?.id}
        testId="seller-courier-delete-dialog"
      />
    </div>
  );
};
